<template>
  <div>
    <v-container class="intake-form-page" fluid>
      <v-row justify="start" class="mx-4">
        <v-col cols="12" xs="12" sm="12" md="12" lg="12" xl="11">
          <v-card>
            <v-app-bar color="primary" dark elevation="0">
              <v-toolbar-title> <v-icon color="white" left> mdi-tag-search</v-icon> Test Kit </v-toolbar-title>
              <v-spacer></v-spacer>
              <div v-if="searchExecuted" class="refreshcontainer">
                <p class="refreshdatatext">Last fetched: {{ searchedTimeStamp }}.</p>
                <v-btn @click="searchRefresh" rounded class="ml-2 px-1 py-6 refreshbtn">
                  <v-icon large>mdi-refresh</v-icon>
                </v-btn>
              </div>
            </v-app-bar>
            <v-toolbar-title>
              <v-row class="mx-5 mt-4">
                <v-col cols="8" xs="7" sm="8" md="6" lg="4">
                  <ScanInput
                    :caseId="storeCaseId"
                    :autoFocus="true"
                    :autoComplete="off"
                    :label="'QR-code / AZV barcode'"
                    :nohint="true"
                    @scan="scan"
                  />
                </v-col>
                <v-col cols="2" xs="2" sm="2" md="3" lg="2">
                  <div class="searchinputstyle">
                    <v-btn @click="resetData"> Reset </v-btn>
                  </div>
                </v-col>
                <v-col cols="12" xs="12" sm="6" md="6" lg="3">
                  <div class="searchinputstyle mtop">
                    <DatePicker
                      :valueFromParent="startDate"
                      label="Start date"
                      keyName="startdate"
                      :required="false"
                      :disabled="disableDatePicker"
                      @updateParentValue="updateStartDate"
                      :clearable="true"
                      :maxDateToday="true"
                      :clearDateValue="clearDate"
                    />
                  </div>
                </v-col>
                <v-col cols="12" xs="12" sm="6" md="6" lg="3">
                  <div class="searchinputstyle mtop">
                    <DatePicker
                      :valueFromParent="endDate"
                      label="End Date"
                      keyName="enddate"
                      :required="false"
                      :disabled="disableDatePicker"
                      @updateParentValue="updateEndDate"
                      :clearable="true"
                      :maxDateToday="true"
                      :clearDateValue="clearDate"
                    />
                  </div>
                </v-col>
              </v-row>
              <v-row class="mx-8 mt-1">
                <v-progress-linear v-if="showLoading" indeterminate></v-progress-linear>
              </v-row>
            </v-toolbar-title>

            <v-row class="mx-4">
              <v-col>
                <v-simple-table outlined>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left col100"></th>
                        <th class="text-left col200">Number</th>
                        <th class="text-left">Registered date</th>
                        <th class="text-left">Sample result date</th>
                        <th class="text-left col200">Payment method</th>
                        <th class="text-left">LinkedBy</th>
                        <th class="text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(kit, i) in testKits" :key="kit.number" @click="clickTestKitItem">
                        <td><v-icon>mdi-test-tube</v-icon></td>
                        <td>{{ kit.number }}</td>
                        <td>{{ kit.registerDateStr }}</td>
                        <!-- <td>{{ kit.sampleResultDateStr }}</td> -->
                        <td>{{ kit.paymentMethod }}</td>
                        <td>{{ kit.linkedBy }}</td>
                        <td>
                          <v-menu bottom left offset-y>
                            <template v-slot:activator="{ on }">
                              <v-btn icon v-on="on" fab class="subject-menu-btn mt-1 me-2" style="background-color: #f0f0f0" small>
                                <v-icon class="mx-auto">mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>

                            <v-list>
                              <v-list-item @click="deleteTestkitDialog(kit)" :disabled="kit.loading || !hasDeletePermission">
                                <v-list-content>
                                  <v-list-item-title> <v-icon left>mdi-delete</v-icon>Delete TestKit</v-list-item-title>
                                  <v-list-item-subtitle v-if="!hasDeletePermission" class="caption ms-8 mt-0">
                                    Can only delete if test kit has no result and user has permission
                                  </v-list-item-subtitle>
                                </v-list-content>
                              </v-list-item>
                              <v-list-item @click="downloadTestkitCertificate(kit, i)" :disabled="kit.loading || !kit.sampleResult">
                                <v-list-content>
                                  <v-list-item-title> <v-icon left>mdi-download</v-icon>Download Certificate</v-list-item-title>
                                  <v-list-item-subtitle v-if="!kit.sampleResult" class="caption ms-8 mt-0">
                                    Certificate available if test kit has result
                                  </v-list-item-subtitle>
                                </v-list-content>
                              </v-list-item>
                              <v-list-item @click="editPaymentMethod(kit, i)">
                                <v-list-content>
                                  <v-list-item-title> <v-icon left>mdi-cash-multiple</v-icon>Edit Payment Method</v-list-item-title>
                                  <v-list-item-subtitle v-if="!kit.sampleResult" class="caption ms-8 mt-0">
                                    Edit payment method available if test kit has result
                                  </v-list-item-subtitle>
                                </v-list-content>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>

            <v-row v-if="noResults">
              <v-col align="center"> No results </v-col>
            </v-row>

            <v-row class="mx-4 mt-2">
              <v-col>
                <v-divider></v-divider>
              </v-col>
            </v-row>

            <v-row>
              <!-- <v-col style="width: 80px" md="1" lg="1"> Display </v-col> -->
              <v-col class="ml-8 mt-2" md="2" lg="2">
                <v-select
                  v-model="startRowsDisplay"
                  :items="rowsItems"
                  @change="rowsPerPageChanged"
                  item-text="rows"
                  item-value="rows"
                  label="Display"
                  class="rowselectstyle"
                  dense
                ></v-select>
              </v-col>
              <v-col v-if="hasTestKits" :slot="totalItems" class="aligncenter mt-2" md="2" lg="2" style="margin-bottom: 17px">
                <div>Total: {{ totalItems }} | {{ viewingItems }}</div>
              </v-col>
              <v-col>
                <v-pagination v-if="hasTestKits" v-model="currentPageNumber" :length="totalPaginationPages" :total-visible="7"></v-pagination>
              </v-col>
              <v-col>
                <v-spacer></v-spacer>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- print component -->
    <PrintLinkedCase v-if="linkedUser" id="printLinkedSubjectAuto" :linkedUser="linkedUser" :lastScannedBarcode="lastScannedBarcode" />

    <!-- DELETE DIALOG -->
    <v-dialog v-model="deleteDialogOpen" max-width="290" persistent>
      <v-card>
        <v-card-title class="headline"> Are you sure? </v-card-title>
        <v-card-text> Test kit data will be deleted from this subject. </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialogOpen = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteTestKit()" :loading="deletingTestKit" :disabled="deletingTestKit">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- payment method modal -->
    <PaymentMethodModal
      v-if="showPaymentMethodModal"
      :showPaymentMethodModal="showPaymentMethodModal"
      :paymentMethodParent="selectedPaymentMethod"
      @submitPaymentMethodParent="submitPaymentMethod"
    />

    <!-- search account and fill scan input with case id -->
    <AccountSearchModal :showModal="showAccountSearchModal" />
  </div>
</template>

<script>
import moment from 'moment';
import AccountSearchModal from '@/components/AccountSearchComponent/account-search-modal';
import ScanInput from '@/components/ScanInputComponent/ScanInput.vue';
import PaymentMethodModal from '@/components/test-kit/components/PaymentMethodModal.vue';
import PrintLinkedCase from '@/components/test-kit/components/PrintLinkedCase.vue';
import SharedFunctions from '@/mixins/SharedFunctions';
import DatePicker from '@/components/form-components/date-picker';
import { mapActions, mapGetters } from 'vuex';

export default {
  mixins: [SharedFunctions],
  components: { AccountSearchModal, DatePicker, PaymentMethodModal, PrintLinkedCase, ScanInput },
  data() {
    return {
      caseIdInput: null,
      caseIdChanged: false,
      caseIdHasError: false,
      clearDate: false,
      currentPageNumber: 1,
      dateChanged: false,
      deleteDialogOpen: false,
      disableDatePicker: false,
      endDate: null,
      endDateIsEmpty: false,
      noResults: false,
      prevEndDate: null,
      hasTestKits: false,
      headers: [
        {
          text: 'Number',
          align: 'start',
          sortable: false,
          value: 'number'
        },
        {
          text: 'Registered date',
          align: 'start',
          sortable: true,
          value: 'registerDate'
        },
        {
          text: 'Payment method',
          align: 'start',
          sortable: false,
          value: 'paymentMethod'
        },
        {
          text: 'Linked by',
          align: 'start',
          sortable: false,
          value: 'linkedBy'
        }
      ],
      rowsItems: [{ rows: 5 }, { rows: 10 }, { rows: 20 }, { rows: 30 }, { rows: 50 }, { rows: 100 }],
      rowsToDisplay: 20,
      rowsToDisplayChanged: false,
      showEditDialog: false,
      startRowsDisplay: { rows: 50 },
      startDate: null,
      startDateIsEmpty: false,
      prevStartDate: null,
      testKits: null,
      allTestkits: [],
      refreshTable: false,
      totalItems: 0,
      totalPaginationPages: 0,
      searchExecuted: false,
      showLoading: false,
      showAccountSearchModal: false,
      searchedTimeStamp: null,
      selectedIdForDelete: null,
      selectedPaymentMethod: null,
      selectedTestKit: null,
      showPaymentMethodModal: false
    };
  },
  watch: {
    currentPageNumber: {
      handler() {
        this.search();
      }
    },
    testKits: {
      handler() {
        this.hasTestKits = this.testKits?.length > 0;
      }
    },
    caseId: {
      handler() {
        console.log(this.caseIdInput);
      }
    }
  },
  computed: {
    hasDeletePermission() {
      if (this.userHasRole('service-center-admin')) return true;
      else return false;
    },
    storeCaseId() {
      return this.$store.state.testTubeLinkStore.currentPatientCaseId;
    },
    testKitData() {
      return this.$store.state.testKitStore.selectedCaseTestKitData;
    },
    viewingItems() {
      let pageNum = this.currentPageNumber;
      let rowsToDisplay = this.rowsToDisplay;
      let fromItemNr = pageNum * rowsToDisplay - (rowsToDisplay - 1); // (from)11 - (to) 20
      let currentPageNrContainsLastItem = this.totalItems <= fromItemNr + (rowsToDisplay - 1);
      let toItemNr = currentPageNrContainsLastItem ? this.totalItems : fromItemNr + (rowsToDisplay - 1);
      // let lastItemCount = pageNum * rowsToDisplay + rowsToDisplay > this.totalItems ? this.totalItems : pageNum * rowsToDisplay + rowsToDisplay;
      return pageNum == 1 ? '1 - ' + rowsToDisplay : fromItemNr + ' - ' + toItemNr;
    }
  },
  created() {
    // clears AccountSearchModal results
    this.$store.commit('accountSearchStore/SET_SEARCHED_ACCOUNTS', []);
    this.$store.commit('accountSearchStore/SET_ACCOUNT_SEARCHED_QUERY', null);
  },
  mounted() {
    this.showLoading = false;
    this.rowsToDisplay = this.$store.state.testKitStore.rowsToDisplay ?? parseInt(this.startRowsDisplay.rows);

    if (this.$store.state.testKitStore.testKits?.length > 0) {
      this.allTestkits = this.$store.state.testKitStore.allTestkits;
      this.currentPageNumber = this.$store.state.testKitStore.currentPageNumber;
      this.testKits = this.$store.state.testKitStore.testKits;
      this.totalItems = this.$store.state.testKitStore.totalItems;
      this.totalPaginationPages = this.$store.state.testKitStore.totalPaginationPages;

      this.startDate = this.$store.state.testKitStore.startDate;
      this.prevStartDate = this.startDate;
      this.endDate = this.$store.state.testKitStore.endDate;
      this.prevEndDate = this.endDate;
      this.setTestKitsForTableDisplay(null, this.testKits);
    } else {
      //this.setDatesToToday();
    }
    this.searchTestkits();
  },
  methods: {
    ...mapActions('testKitStore', ['fetchTestKits']),
    ...mapGetters('testTubeLinkStore', ['visitorQr', 'azvNumber', 'tubeBarCode']),
    clickTestKitItem() {
      this.showEditDialog = true;
    },
    caseIdInputChanged(value) {
      this.caseIdInput = value;
      this.caseIdChanged = true;
      this.caseIdHasError = !this.isValidCaseIdInput();
      if (this.caseIdHasError) return;

      if (this.checkIfSearchByCaseId()) {
        this.disableDatePicker = true;
      } else {
        this.disableDatePicker = false;
      }
    },
    async downloadTestkitCertificate(kit, i) {
      if (kit.patientCaseId) {
        this.$store.commit('SET_SHOW_LOADER', {
          action: true,
          text: 'One momeent...'
        });

        await this.$store.dispatch('testKitStore/getPatientTestKitInfo', kit.patientCaseId).catch((err) => {
          console.log(err);
          let payload = {
            open: true,
            text: err,
            color: 'error'
          };
          this.$store.commit('SET_SNACKBAR', payload);
        });

        const payload = {
          caseId: kit.patientCaseId,
          teskKitNumber: kit.number
        };

        this.$store.commit('testKitStore/SET_TESTKIT_RESULT_DOWNLOAD_LOADING', { index: i, action: true, kitId: kit.id });
        this.$store.dispatch('testKitStore/downloadTestkitCertificate', payload).then(() => {
          this.$store.commit('testKitStore/SET_TESTKIT_RESULT_DOWNLOAD_LOADING', { index: i, action: false, kitId: kit.id });

          this.$store.commit('SET_SHOW_LOADER', {
            action: false,
            text: null
          });
        });
      } else {
        let payload = {
          open: true,
          text: 'Testkit record is missing patientCaseId.',
          color: 'error'
        };
        this.$store.commit('SET_SNACKBAR', payload);
        return;
      }
    },
    deleteTestkitDialog(kit) {
      this.selectedIdForDelete = kit.id;
      this.caseIdInput = kit.patientCaseId;
      this.deleteDialogOpen = true;
    },
    async deleteTestKit() {
      this.$store.commit('SET_SHOW_LOADER', {
        action: true,
        text: 'One momeent...'
      });

      try {
        this.deletingTestKit = true;
        await this.$store.dispatch('testKitStore/deleteTestkit', this.selectedIdForDelete);
        this.deletingTestKit = false;
        this.deleteDialogOpen = false;
        this.dataLoaded = false;
        await this.getTestKits({ refreshData: true });
      } catch (error) {
        console.log(error);
        this.$store.commit('SET_SHOW_LOADER', {
          action: true,
          text: ''
        });
      } finally {
        this.$store.commit('SET_SHOW_LOADER', {
          action: false,
          text: null
        });
      }
    },
    // Guid validation
    isValidCaseIdInput() {
      try {
        if (this.caseIdInput?.length == 36 && (this.caseIdInput?.match(/-/g) || []).length == 4) {
          let arr = this.caseIdInput.split('-');
          if (arr.length == 5) {
            //88d4acb6-8814-4296-bd32-aa79cf4bf670
            if (
              arr[0].match(/[0-9a-zA-Z]/g)?.length == 8 &&
              arr[1].match(/[0-9a-zA-Z]/g)?.length == 4 &&
              arr[2].match(/[0-9a-zA-Z]/g)?.length == 4 &&
              arr[3].match(/[0-9a-zA-Z]/g)?.length == 4 &&
              arr[4].match(/[0-9a-zA-Z]/g)?.length == 12
            ) {
              return true;
            }
          }
          return false;
        }

        return false;
      } catch (error) {
        console.log('error isValidCaseIdInput: ' + error);
      }
    },
    validateInput() {
      let res = this.isValidCaseIdInput() || this.caseIdInput == null;
      return res; //if 0 then allow search without caseid. Else input is invalid.
    },
    checkIfSearchByCaseId() {
      let len = this.caseIdInput?.length ?? 0;
      let dashesInUUID = 4;
      let UUIDLength = 32 + dashesInUUID;
      return len == UUIDLength;
    },
    resetData() {
      this.testKits = null;
      this.allTestkits = [];
      this.currentPageNumber = 1;
      this.totalItems = 0;
      this.caseIdHasError = false;

      this.$store.state.testKitStore.testKits = null;
      this.$store.state.testKitStore.allTestkits = null;
      this.$store.state.testKitStore.totalItems = 0;
      this.$store.state.testKitStore.totalPaginationPages = 0;
      this.$store.state.testKitStore.currentPageNumber = 0;
      this.$store.state.testKitStore.rowsToDisplay = 0;
      this.$store.state.testKitStore.startDate = null;
      this.$store.state.testKitStore.endDate = null;
    },
    rowsPerPageChanged(rows) {
      this.rowsToDisplay = parseInt(rows);
      this.rowsToDisplayChanged = true;
      this.search();
      this.rowsToDisplayChanged = false;
    },
    setDatesToToday() {
      this.startDate = moment(); //.format('LL');
      this.prevStartDate = this.startDate;
      this.endDate = moment(); //.format('LL');
      this.prevEndDate = this.endDate;
    },
    searchRefresh() {
      this.$store.commit('SET_SHOW_LOADER', {
        action: true,
        text: 'One momeent...'
      });
      this.refreshTable = true; //
      this.getTestKits({ refreshData: true }).finally(() => {
        this.$store.commit('SET_SHOW_LOADER', {
          action: false,
          text: null
        });
      });
    },
    search() {
      if (this.validateInput()) {
        this.searchTestkits();
      } else {
        this.caseIdHasError = true;
        let msg = this.caseIdInput?.length != 36 ? 'Input length is ' + this.caseIdInput?.length + '.' : '';
        let msg2 = (this.caseIdInput?.match(/-/g) || []).length !== 4 ? 'Invalid format.' : '';
        this.$store.dispatch('showError', 'Case id is invalid. ' + msg + msg2);
        console.log('Case id is invalid, can not search.');
      }
    },
    updateStartDate(startDate) {
      if (startDate) {
        this.startDateIsEmpty = false;
        this.startDate = null; // reset first due to bug.
        this.startDate = moment(startDate, 'YYYY-MM-DD').add(-1, 'days'); // when cleared and reset on same date before cleared, the date will not load.
        // check diff to see if startdate is newest. If true, make sure enddate is always newest.
        if (this.startDate.diff(this.endDate, 'minutes') > 0) {
          if (this.startDate._d.getDate() > this.endDate._d.getDate()) {
            this.endDate = this.startDate;
          }
        }
      } else {
        this.startDateIsEmpty = true;
        this.dateChanged = true;
        this.startDate = null;
      }

      if (!this.$store.state.accountSearchStore.hasSelectedCase) {
        // prevent search when startDate is cleared by selecting a case from search modal.
        this.searchTestkits();
        this.$store.commit('testTubeLinkStore/SET_CURRENT_PATIENT_CASE_ID', null);
      }
    },
    updateEndDate(endDate) {
      if (endDate) {
        this.endDateIsEmpty = false;
        this.endDate = null; // reset first due to clear date bug.
        this.endDate = moment(endDate, 'YYYY-MM-DD').add(-1, 'days');

        // check diff to see if startdate is newest. If true, make sure enddate is always newest.
        if (this.endDate.diff(this.startDate, 'minutes') < 0) {
          if (this.endDate._d.getDate() < this.startDate._d.getDate()) {
            this.startDate = this.endDate;
          }
        }
      } else {
        this.endDateIsEmpty = true;
        this.dateChanged = true;
        this.endDate = null;
      }

      if (!this.$store.state.accountSearchStore.hasSelectedCase) {
        // prevent search when endDate is cleared by selecting a case from search modal.
        this.searchTestkits();
        this.$store.commit('testTubeLinkStore/SET_CURRENT_PATIENT_CASE_ID', null);
      }
    },
    prepareTestkitObjForDisplay(testkits) {
      if (testkits) {
        for (var i = 0; i < testkits.length; i++) {
          let current = testkits[i];
          if (current.tags !== undefined) {
            let tagObject = JSON.parse(current.tags);
            current['paymentMethod'] = tagObject.paymentMethod;
          }

          if (current.registerDate !== undefined)
            current.registerDateStr = this.formatDateTime(current.registerDate); //Custom(current.registerDate, 'YYYY-MM-DD HH:mm');
          
          // if (current.sampleResultDate !== undefined)
          // {
          //   current.sampleResultDateStr = this.formatDateTime(current.sampleResultDate);
          // }
        }
      }
    },
    setTestKitsForTableDisplay(testkitsData, cached) {
      this.testKits = cached ?? testkitsData.data;
      let newTotalItems = testkitsData?.dataSetRowCount ?? this.$store.state.testKitStore.totalItems;
      if (this.totalItems != 0) {
        this.totalItemsChanged = newTotalItems !== this.totalItems; // If testkits are added, the total will change. Data needs to be refreshed.
      }

      this.totalItems = newTotalItems;
      this.setTestKitNumbering(this.testKits);
      this.allTestkits[this.currentPageNumber] = this.testKits;
      this.totalPaginationPages = this.totalItems / this.rowsToDisplay;
      this.totalPaginationPages = parseInt(this.totalPaginationPages % 1 > 0 ? this.totalPaginationPages + 1 : this.totalPaginationPages);
      this.prepareTestkitObjForDisplay(this.testKits);
      //this.checkUniqueCaseNumbers();

      // cache page
      this.$store.state.testKitStore.testKits = this.testKits;
      this.$store.state.testKitStore.allTestkits = this.allTestkits;
      this.$store.state.testKitStore.totalItems = this.totalItems;
      this.$store.state.testKitStore.totalPaginationPages = this.totalPaginationPages;
      this.$store.state.testKitStore.currentPageNumber = this.currentPageNumber;
      this.$store.state.testKitStore.rowsToDisplay = this.rowsToDisplay;
      this.$store.state.testKitStore.startDate = this.startDate;
      this.$store.state.testKitStore.endDate = this.endDate;
    },
    setTestKitNumbering(testkits) {
      if (testkits) {
        let startNr = this.currentPageNumber == 1 ? 1 : this.currentPageNumber * 10 - 9; //next set wil not begin at 10 but at 11
        for (var i = 0; i < testkits.length; i++) {
          let kit = testkits[i];
          kit['nr'] = startNr;
          startNr += 1;
        }
      }
    },
    checkUniqueCaseNumbers() {
      for (var i = 1; i < this.allTestkits.length; i++) {
        let currentSet = this.allTestkits[i];
        let nextSet = this.allTestkits[i + 1];
        this.findDuplicatesInSameSet(currentSet);
        if (nextSet != undefined) {
          this.findDuplicates(currentSet, nextSet);
        }
      }
    },
    findDuplicatesInSameSet(firstSet) {
      try {
        let hasDuplicates = false;
        for (var i = 0; i < firstSet.length; i++) {
          if (firstSet[i + 1] != undefined) {
            if (firstSet[i].number === firstSet[i + 1].number) {
              hasDuplicates = true;
              console.log(
                'Duplicate found in same set: testkitnr: ' +
                  firstSet[i].nr +
                  '(' +
                  firstSet[i].number +
                  ') & testkitnr: ' +
                  firstSet[i + 1].nr +
                  '(' +
                  firstSet[i + 1].number +
                  ')'
              );
            }
          }
        }
        if (hasDuplicates) console.log('Duplicates found in same set! Check console logs');
      } catch (error) {
        console.log(error);
      }
    },
    findDuplicates(firstSet, secondSet) {
      try {
        let hasDuplicates = false;

        for (var i = 0; i < firstSet.length; i++) {
          for (var j = 0; j < secondSet.length; j++) {
            if (firstSet[i].number == secondSet[j].number) {
              hasDuplicates = true;
              console.log(
                'Duplicate found: testkitnr: ' +
                  firstSet[i].nr +
                  ' (' +
                  firstSet[i].number +
                  ') & testkitnr: ' +
                  secondSet[j].nr +
                  '(' +
                  secondSet[j].number +
                  ')'
              );
              // console.log('total items: ' + this.totalItems);
            }
          }
        }

        if (hasDuplicates) console.log('Duplicates found in sets. New data possibly exists in the database. Check console logs.');
      } catch (error) {
        console.log(error);
      }
    },
    resetDates() {
      this.startDate = null;
      this.prevStartDate = null;
      this.endDate = null;
      this.prevEndDate = null;
      this.$store.state.testKitStore.startDate = null;
      this.$store.state.testKitStore.endDate = null;
    },
    async scan(value) {
      try {
        this.$store.commit('SET_SHOW_LOADER', { action: true, text: 'Loading ....' });
        this.clearDate = true;
        this.resetDates();
        var codeType = await this.$store.dispatch('testTubeLinkStore/getCodeType', { input: value });
        if (codeType.type == this.visitorQr()) {
          this.caseIdInput = codeType.caseId;
          this.caseIdChanged = true;
          this.searchTestkits();
        } else if (codeType.type == this.azvNumber()) {
          this.$store.dispatch('testTubeLinkStore/scan', value).then((res) => {
            if (res.error) {
              this.$store.commit('SET_SNACKBAR', { open: true, text: res.error, color: 'error' });
            } else {
              this.setTestKitsForTableDisplay(null, res.displaySubjectData);
            }
          });
        }

        this.clearDate = false;
      } catch (error) {
        error.message ? console.log(error.message) : console.log(error);
        this.$store.commit('SET_SHOW_LOADER', {
          action: true,
          text: 'Scan failed'
        });
      }
    },

    async searchTestkits() {
      try {
        if (
          this.startDate?.diff(this.prevStartDate, 'minutes') != 0 ||
          this.endDate?.diff(this.prevEndDate, 'minutes') != 0 ||
          this.refreshTable ||
          this.caseIdChanged
        ) {
          this.resetData();
          this.totalItemsChanged = false;
          this.refreshTable = false;
        }

        this.prevStartDate = this.startDate;
        this.prevEndDate = this.endDate;

        if (!this.rowsToDisplayChanged && !this.dateChanged && this.allTestkits.length > 0) {
          if (this.allTestkits[this.currentPageNumber] != undefined) {
            this.setTestKitsForTableDisplay(null, this.allTestkits[this.currentPageNumber]); // use cached testkits for loading previous paginated table.
            return;
          }
        }

        this.showLoading = true;
        if (!this.startDateIsEmpty || !this.endDateIsEmpty) this.caseIdInput = null;

        await this.getTestKits({
          caseId: this.caseIdInput,
          startDate: this.startDateIsEmpty ? null : this.startDate,
          endDate: this.endDateIsEmpty ? null : this.endDate,
          rowsToDisplay: this.rowsToDisplay,
          currentPageNumber: this.currentPageNumber
        });

        this.$store.commit('SET_SHOW_LOADER', { action: false, text: null });
      } catch (error) {
        console.log(error);
      } finally {
        this.$store.commit('accountSearchStore/SET_HAS_SELECTED_CASE', false);
      }
    },
    async getTestKits(searchObj) {
      return this.fetchTestKits(searchObj)
        .then((res) => {
          // results are set in store.state.testKitStore.
          if (res) {
            this.noResults = false;
            this.setTestKitsForTableDisplay(res.data);
          } else {
            this.noResults = true;
          }
          this.showLoading = false;
          this.caseIdChanged = false;
          this.searchExecuted = true;
          this.searchedTimeStamp = this.formatDateTime(new Date()); //.format("YYYY-MM-DD HH:mm ");
        })
        .catch((error) => {
          console.log(error);
          this.showLoading = false;
          this.caseIdChanged = false;
          this.noResults = true;
        });
    },
    async editPaymentMethod(kit, index) {
      this.selectedIndex = index;
      this.selectedPaymentMethod = this.getPaymentMethod(kit.tags);
      this.showPaymentMethodModal = true;
      this.selectedTestKit = kit;
    },
    getPaymentMethod(tags) {
      if (!tags) return false;
      const tagsArr = JSON.parse(tags);
      if (tagsArr && tagsArr.paymentMethod) return tagsArr.paymentMethod;
      else return false;
    },
    toggleShowAccountSearchModal() {
      this.showAccountSearchModal = !this.showAccountSearchModal;
    },
    async submitPaymentMethod(value) {
      try {
        this.$store.commit('SET_SHOW_LOADER', {
          action: true,
          text: 'One momeent...'
        });

        let testKitData = JSON.parse(JSON.stringify(this.selectedTestKit)); //this.testKitData[0])); //this.testKitData[this.selectedIndex]));
        let tags = JSON.parse(testKitData.tags);
        tags.paymentMethod = value;
        testKitData.tags = JSON.stringify(tags);
        await this.$store
          .dispatch('testKitStore/updatePatientTestKitInfo', testKitData)
          .then((res) => {
            this.caseIdInput = res.patientCaseId;
          })
          .catch((err) => {
            this.$store.commit('SET_SNACKBAR', {
              open: true,
              text: err,
              color: 'error'
            });
          });

        this.getTestKits({ refreshData: true }).finally(() => {
          this.$store.commit('SET_SHOW_LOADER', {
            action: false,
            text: null
          });
        });
      } catch (error) {
        console.log(error);
      }
    }
  }
};
</script>

<style>
.col100 {
  width: 100px;
}
.col200 {
  width: 200px;
}
.rowselectstyle {
  width: 70px;
}
.searchinputstyle {
  /* width: 130px; */
  margin-top: 10px;
}
.refreshbtn {
  color: lightgray !important;
  background-color: white !important;
  min-width: 50px !important;
}
.refreshcontainer {
  display: flex;
  align-items: center;
}
.refreshdatatext {
  font-size: 15px;
  float: left;
  margin-bottom: 0px !important;
  margin-left: 5px;
}
.aligncenter {
  display: flex;
  align-items: center;
}

@media only screen and (max-width: 959px) {
  .mtop {
    margin-top: -5px;
  }
}

@media only screen and (min-width: 960px) {
  .mtop {
    margin-top: 20px;
  }
}
</style>